/**
 * @fileoverview メッセージバブルコンポーネント
 * @description チャットのメッセージ表示
 */
import type { ChatSource } from '@karosorabei/shared'

import { cn } from '@/lib/utils'
import { CharacterAvatar, type CharacterType } from '@/components/chat/character-avatar'
import { GroupedSourceList } from '@/components/chat/source-card'

/**
 * メッセージの種類
 */
export type MessageRole = 'user' | 'assistant'

/**
 * メッセージオブジェクトの型（useChatから渡される）
 */
export interface ChatMessageObject {
  /** メッセージID */
  id: string
  /** 送信者の役割 */
  role: MessageRole
  /** メッセージ内容 */
  content: string
  /** AIキャラクター（assistantの場合） */
  character?: CharacterType
  /** ソース情報（assistantの場合） */
  sources?: ChatSource[]
  /** クエリ拡張で生成されたキーワード（ハイライト用） */
  expandedKeywords?: string[]
  /** 作成日時 */
  createdAt: Date
}

/**
 * MessageBubbleのprops型
 */
export interface MessageBubbleProps {
  /** メッセージオブジェクト（useChatから渡す場合） */
  message?: ChatMessageObject
  /** メッセージ送信者の役割（直接指定する場合） */
  role?: MessageRole
  /** メッセージ内容（直接指定する場合） */
  content?: string
  /** AIキャラクター種別（role='assistant'の場合） */
  character?: CharacterType
  /** ソース情報（AIメッセージの場合） */
  sources?: ChatSource[]
  /** クエリ拡張で生成されたキーワード（ハイライト用） */
  expandedKeywords?: string[]
  /** カスタムクラス名 */
  className?: string
}

/**
 * メッセージバブルコンポーネント
 * @description ユーザーとAIのメッセージを表示
 * @example
 * <MessageBubble role="user" content="質問です" />
 * <MessageBubble role="assistant" character="sorabei" content="回答です" sources={[...]} />
 */
export function MessageBubble({
  message,
  role: roleProp,
  content: contentProp,
  character: characterProp = 'sorabei',
  sources: sourcesProp,
  expandedKeywords: expandedKeywordsProp,
  className,
}: MessageBubbleProps) {
  // メッセージオブジェクトから値を取得、または直接指定された値を使用
  const role = message?.role ?? roleProp ?? 'user'
  const content = message?.content ?? contentProp ?? ''
  const character = message?.character ?? characterProp
  const sources = message?.sources ?? sourcesProp
  const expandedKeywords = message?.expandedKeywords ?? expandedKeywordsProp ?? []
  const isUser = role === 'user'

  return (
    <div className={cn('flex flex-col gap-2', isUser ? 'items-end' : 'items-start', className)}>
      {/* AIメッセージの場合はアバターを表示 */}
      {!isUser && <CharacterAvatar character={character} showName />}

      {/* メッセージバブル */}
      <div
        className={cn(
          'max-w-[280px] rounded-2xl px-4 py-4',
          isUser ? 'bg-[#3D8A5A] text-white' : 'bg-white text-[#1A1918]'
        )}
      >
        {/* メッセージ本文 */}
        <p className="text-sm leading-[1.6] whitespace-pre-wrap">{content}</p>

        {/* ソース情報（AIメッセージの場合） */}
        {!isUser && sources && sources.length > 0 && (
          <div className="mt-3 pt-3 border-t border-gray-100">
            <GroupedSourceList sources={sources} expandedKeywords={expandedKeywords} />
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * ローディング中のメッセージバブル
 */
export function LoadingBubble({
  character = 'sorabei',
  className,
}: {
  character?: CharacterType
  className?: string
}) {
  return (
    <div className={cn('flex flex-col gap-2 items-start', className)}>
      <CharacterAvatar character={character} showName />
      <div className="max-w-[280px] rounded-2xl bg-white px-4 py-4">
        <div className="flex gap-1">
          <span className="animate-bounce h-2 w-2 rounded-full bg-[#3D8A5A] [animation-delay:0ms]" />
          <span className="animate-bounce h-2 w-2 rounded-full bg-[#3D8A5A] [animation-delay:150ms]" />
          <span className="animate-bounce h-2 w-2 rounded-full bg-[#3D8A5A] [animation-delay:300ms]" />
        </div>
      </div>
    </div>
  )
}
